import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import React, { FC, ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { TaskId } from 'api/models';
import { Task as TaskApi } from 'api/tasks';
import { Collapse, Panel, PanelHeaderTitle } from 'components/atoms/collapse';
import { IconShell } from 'components/atoms/icon-shell';
import { CompanyTasksEmptyState } from 'components/organisms/home/company-tasks/empty-state';
import { NoResultEmptyState } from 'components/organisms/home/company-tasks/no-result';
import {
  Filter as FilterT,
  getDataByGroupByFilter,
  getDataByView,
} from 'components/organisms/home/overview/get-render-data';
import styles from 'components/organisms/home/overview/overview.module.css';
import { useCompanyTasksStore } from 'data/home/hooks';
import { Task } from 'data/tasks';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useUser } from 'hooks/use-user';
import {
  GroupByFilterForCompanyTasks,
  GroupByValue,
  ViewFilter,
  ViewValue,
} from '../filter';
import { Bar } from '../filter-bar';
import { Skeleton } from '../loader';
import { Section } from '../section';

const Wrapper = styled.div`
  padding: var(--layout-01);
`;

type Filter = Omit<FilterT, 'assignedTo'>;

const DEFAULT_FILTER_STATE: Filter = {
  view: ViewValue.Incomplete,
  groupBy: GroupByValue.DueDate,
};

interface ContentProps {
  data: any[];
}

const constructTitle = (title: ReactNode, count = 0): ReactNode => {
  return (
    <>
      {title}
      {' · '}
      {count}
    </>
  );
};

const Content: FC<ContentProps> = ({ data }) => {
  const [activeKeys, setActiveKeys] = useState<string | string[]>(
    data.map((r) => r.key),
  );
  const navigateToTask = useNavigateToTask();
  const openTask = (task: Task | TaskApi) => {
    const _projectId =
      typeof task.project === 'number' ? task.project : task.project?.projectId;

    navigateToTask(TaskId(_projectId, task.taskId));
  };

  return (
    <Collapse
      key="list"
      className={styles.collapse}
      ghost
      defaultActiveKey={activeKeys}
      activeKey={activeKeys}
      onChange={(selected) => setActiveKeys(selected)}
      expandIconPosition="right"
      expandIcon={({ isActive }) => {
        return isActive ? <CaretUp16 /> : <CaretDown16 />;
      }}
    >
      {data.map((d) => {
        return d.items.length > 0 ? (
          <Panel
            key={d.key}
            showArrow
            header={
              <PanelHeaderTitle
                icon={<IconShell size="large" icon={d.icon} />}
                title={constructTitle(d.label, d.items.length)}
              />
            }
          >
            <Section data={d.items} onItemClick={openTask} />
          </Panel>
        ) : (
          <></>
        );
      })}
    </Collapse>
  );
};

export const CompanyTasks: FC = () => {
  const { isLoading, data } = useCompanyTasksStore();
  const [filter, setFilter] = useState<Filter>(DEFAULT_FILTER_STATE);

  const handleViewChange = (selected) => {
    setFilter({ ...filter, view: selected.value });
  };
  const handleGroupByChange = (selected) => {
    setFilter({ ...filter, groupBy: selected.value });
  };

  const user = useUser();

  const getResult = useCallback(() => {
    return getDataByGroupByFilter(
      getDataByView(data, filter.view),
      filter,
      user,
    );
  }, [filter, data, user]);

  return (
    <>
      <Bar
        primary={
          <>
            <ViewFilter onChange={handleViewChange} />
            <GroupByFilterForCompanyTasks onChange={handleGroupByChange} />
          </>
        }
      />
      <Wrapper>
        <Skeleton loading={isLoading}>
          {data.length > 0 ? (
            getResult() && getResult().length > 0 ? (
              <Content data={getResult()} />
            ) : (
              <NoResultEmptyState />
            )
          ) : (
            <CompanyTasksEmptyState />
          )}
        </Skeleton>
      </Wrapper>
    </>
  );
};
